import styled from 'styled-components';

import { colors, cssBreakpoints, fontWeights, textStyles } from 'theme/theme';

export const StyledTable = styled.table<{
  $fixed: boolean;
  $widths: (number | string)[];
  $reverseBg?: boolean;
  $simple?: boolean;
  $lastColumnAction?: boolean;
  $continuousHeaderBorder?: boolean;
}>`
  display: block;
  width: 100%;
  text-align: left;
  overflow: auto;
  border-spacing: 0;
  ${textStyles.bodySmall};
  ${(props) =>
    props.$fixed &&
    `
    table-layout: fixed;
  `}

  thead {
    display: none;
    th {
      position: relative;
      font-size: 14px;
      color: ${colors.lightContentGrey};
      font-weight: ${fontWeights.semiBold};
      border-bottom: ${(props) =>
        props.$continuousHeaderBorder
          ? `1px solid ${colors.elementGrey}`
          : 'none'};
      padding-top: 10px;
      padding-bottom: 9px;
      &::before {
        content: '';
        display: ${(props) =>
          props.$continuousHeaderBorder ? 'none' : 'inline'};
        position: absolute;
        bottom: 0;
        left: 0;
        width: calc(100% - 40px);
        height: 1px;
        background: ${colors.elementGrey};
      }
      &:last-child {
        padding-right: 12px;
        &::before {
          width: 100%;
          ${(props) =>
            props.$lastColumnAction &&
            `
            background: none;
          `}
        }
      }
      &:first-child:not(:last-child) {
        &::before {
          left: 0;
          width: calc(100% - 40px);
        }
      }
    }
  }

  tbody {
    border: none;
  }

  tbody,
  th,
  td,
  tr {
    display: block;
  }

  tr {
    float: left;
    width: 100%;
    margin: 0;
    border-top: 1px solid ${colors.elementGrey};
    &:last-of-type {
      border-bottom: 1px solid ${colors.elementGrey};
    }

    td {
      padding: 8px 0 8px 100px;
      float: left;
      width: 100%;
      &::before {
        height: 100%;
        width: 30%;
        display: block;
        float: left;
        padding: 8px 0 8px 0;
        clear: both;
        margin: -8px 0 0 -100px;
        color: ${colors.mediumContentGrey};
        font-weight: ${fontWeights.semiBold};
      }
    }

    td,
    th {
      padding-right: 40px;
    }
  }

  @media print, (min-width: ${cssBreakpoints.mdUp}) {
    display: table;
    border: none;

    thead {
      display: table-header-group;
    }

    th,
    td {
      display: table-cell;

      ${(props) =>
        props.$widths
          .map(
            (width, index) => `
      &:nth-child(${index + 1}) {
        width: ${width}${typeof width === 'number' ? 'px' : ''};
      }
      `,
          )
          .join(' ')}
    }

    tbody {
      display: table-row-group;
      ${(props) =>
        !props.$simple &&
        `
        tr {
          background: ${colors.backgroundGrey};

          &:nth-child(${props.$reverseBg ? 'odd' : 'even'}) {
            background: white;
          }
        }
      `}
    }

    tr {
      display: table-row;
      border: none;
      float: none;
      margin: 0;

      td {
        position: relative;
        padding: ${(props) => (props.$simple ? 8 : 16)}px 0;
        margin: 0;
        float: none;
        width: auto;
        border: 0;

        &:first-child {
          &::after {
            width: 100%;
          }

          padding-left: 6px;
        }

        &::before {
          padding: 0;
        }

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          right: 0;
          width: 100%;
          height: 1px;
          ${(props) =>
            props.$simple
              ? `
            border-bottom: 1px dashed ${colors.selectionGrey};
            `
              : `
            background: #ebeced;
          `}
        }
      }
    }
  }
`;

export const Td = styled.td<{ $heading: string }>`
  @media print, (min-width: ${cssBreakpoints.mdUp}) {
    vertical-align: top;
  }
  &::before {
    content: '${(props) => props.$heading}';
    @media print, (min-width: ${cssBreakpoints.mdUp}) {
      content: '';
    }
  }
`;
